import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';

import { RichText } from 'components/ContentfulContent/RichText/RichText';
import { getCFImageHeightFromWidth } from 'lib/images/imageSizeHelper';
import {
  expandTextMacros,
  containsDateMacros,
} from 'lib/text-macros/textMacroHelper';

import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import type {
  IStageBadge,
  IStageBadgeFields,
  IStageLogo,
  IStageLogoFields,
} from 'types/generated/contentful';

const BADGE_IMAGE_WIDTH = 100;

const BadgeWrapper = styled('div')`
  margin-bottom: 8px;
  margin-top: 8px;

  :nth-last-child(1) {
    margin: 0;
  }

  img {
    display: block;
    width: 100px;
    height: 100px;
    margin-left: 10px;
  }
`;

// Note: The renderText method will change all newlines and change them into br tags.
// If that is not done, react will choke on some browsers as the newline will cause a
// plethora of hydration errors as some browser will filter them out and then you have
// a gross mismatch of content.
const richtextOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_6]: (_, children) => (
      <Typography
        variant="h6"
        sx={{
          textAlign: 'center',
          fontSize: 16,
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          marginBottom: 2,
          fontSize: 14,
          minWidth: 250,

          '& a': {
            fontWeight: 500,
            '&:hover': {
              opacity: '0.75',
            },
          },
        }}
      >
        {children}
      </Typography>
    ),
  },

  renderText: (text) => {
    return text.split('\n').reduce((children: any, textSegment, index) => {
      const expanded = expandTextMacros(textSegment);
      const segment = containsDateMacros(textSegment) ? (
        <span key={`macro-${index}`} suppressHydrationWarning>
          {expanded}
        </span>
      ) : (
        expanded
      );
      return [...children, index > 0 && <br key={index} />, segment];
    }, []);
  },
};

interface BadgeProps {
  content: IStageBadge | IStageLogo | undefined;
  sx?: {};
}

export const Badge = (props: BadgeProps) => {
  const [open, setOpen] = useState(false);

  const { content, sx = {} } = props;
  if (!content) return null;

  const badgeFields = content?.fields as IStageBadgeFields | IStageLogoFields;

  const src = badgeFields?.image?.fields.file?.url?.toString();
  const alt = badgeFields?.image?.fields.description?.toString() || '';

  if (!badgeFields || !badgeFields.image || !src) return null;

  const width = BADGE_IMAGE_WIDTH;
  const height = getCFImageHeightFromWidth(badgeFields.image, width);

  const isUsingLink = !!badgeFields.link;
  const isUsingPopupContent = !isUsingLink && !!badgeFields.popupContent;

  if (!src) return null;

  if (isUsingLink) {
    return (
      <BadgeWrapper>
        <Link href={badgeFields.link!} prefetch={false}>
          <Image style={sx} src={src} height={height} width={width} alt={alt} />
        </Link>
      </BadgeWrapper>
    );
  }

  if (isUsingPopupContent) {
    return (
      <BadgeWrapper>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Tooltip
            onClose={() => setOpen(false)}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            arrow
            title={
              <RichText
                document={badgeFields.popupContent}
                options={richtextOptions}
              />
            }
            slotProps={{
              popper: {
                disablePortal: true,
                popperOptions: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      enabled: true,
                      options: {
                        altAxis: true,
                        altBoundary: false,
                        tether: true,
                        rootBoundary: 'viewport',
                      },
                    },
                  ],
                },
              },
            }}
          >
            <Image
              style={sx}
              src={src}
              height={height}
              width={width}
              alt={alt}
              onClick={() => setOpen(!open)}
            />
          </Tooltip>
        </ClickAwayListener>
      </BadgeWrapper>
    );
  }

  return (
    <BadgeWrapper>
      <Image style={sx} src={src} height={height} width={width} alt={alt} />
    </BadgeWrapper>
  );
};
